import log from "loglevel";
import './liveDashboard.styles.scss';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useParams } from "react-router-dom";
import * as constants from "Constants";
import { Breadcrumb, Button, Spinner, Dropdown, DropdownButton, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Skeleton } from "primereact/skeleton";
import { InputText } from "primereact/inputtext";
import { format } from 'date-fns';
import Countdown from 'react-countdown';
import moment from 'moment';
import httpService from 'services/httpService';
import TableComponent from '../AllSubmissions/TableComponent';
import { useDebounce } from "use-debounce";
import { MultiSelect } from 'primereact/multiselect';
import displayGlobalMessage from "Utils/DisplayGlobalMessage";
import fileDownload from "js-file-download";
import EditableCell from "./EditableCell";
import countdownRenderer from "Components/Common/CountDownRenderer";
import { RefreshProvider, CountdownDisplay, RefreshButton } from "./RefreshContext";

function LiveDashboard() {
    const { courseId, examId } = useParams();
    const location = useLocation();
    const [examTitle, setExamTitle] = useState("");
    const segments = location.pathname.split('/');
    segments.pop();
    segments.pop();
    const old_path = segments.join('/');
    const [studentsResponseData, setStudentsResponseData] = useState([]);
    const [ongoingStudentsCount, setOngoingStudentsCount] = useState(0);
    const [ipRange, setIpRange] = useState("");
    const [finishedStudentsCount, setFinishedStudentsCount] = useState(0);
    const [examStartTime, setExamStartTime] = useState(null);
    const [examEndTime, setExamEndTime] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
    const [lazyLoading, setLazyLoading] = useState(false);
    const [data, setData] = useState([]);
    const [sortField, setSortField] = useState(
        sessionStorage.getItem("sortField") || "is_approved"
    );
    const [sortOrder, setSortOrder] = useState(
        sessionStorage.getItem("sortOrder") || 1
    );
    const [querySortOrder, setQuerySortOrder] = useState(
        sessionStorage.getItem("querySortOrder") || ""
    );
    const [searchQuery, setSearchQuery] = useState(""); //Used for filtering
    const [debouncedSearchQuery] = useDebounce(searchQuery, 500); //Used for debouncing the search query
    const [isNewData, setIsNewData] = useState(false);

    const rows = 100;

    const [selectedRows, setSelectedRows] = useState([]);

    const [visibleColumns, setVisibleColumns] = useState(
        JSON.parse(sessionStorage.getItem("visibleColumns") || "[]") || []
    );

    const hiddenColumns = [
        { name: "Roll No" },
        { name: "Name" },
        { name: "NewIP" },
    ];

    // This useEffect is used to store the state of the columns which are to be displayed in the dashboard
    useEffect(() => {
        sessionStorage.setItem("visibleColumns", JSON.stringify(visibleColumns));
    }, [visibleColumns]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    // To get the title of the exam
    useEffect(() => {
        if (examId) {
            const fetchData = async () => {
                try {
                    var url = constants.base_url + "/lab/all/" + examId + `?courseId=${courseId}`;
                    const response = await httpService.get(url);
                    setIpRange(response.data.lab?.ip_range ? response.data.lab.ip_range : "0.0.0.0-255.255.255.255,");
                    setExamTitle(response.data.lab.title);
                    setExamEndTime(response.data.lab.exam_end_time)
                    setExamStartTime(response.data.lab.exam_start_time)
                }
                catch (error) {
                    log.log(error);
                    if (error?.response?.status === 404) {
                        displayGlobalMessage("Couldn't find the lab", "error");
                    }
                }
            }
            fetchData();
        }
    }, [examId]);

    // Define fetchData as a useCallback function so it can be passed to other components without recreating on every render
    const fetchData = useCallback(async () => {
        try {
            sessionStorage.setItem("sortField", sortField);
            sessionStorage.setItem("sortOrder", sortOrder);
            sessionStorage.setItem("querySortOrder", querySortOrder);
            var url =
                constants.base_url +
                "/exam/" + examId + "/proctor" +
                "?page=" +
                1 +
                "&page_size=" +
                + `${ongoingStudentsCount + finishedStudentsCount}` +
                "&search_query=" +
                debouncedSearchQuery +
                "&sort=" +
                querySortOrder +
                sortField;
            setLazyLoading(true);
            const response = await httpService.get(url)
            setStudentsResponseData(response.data.student_exam.results);
            setOngoingStudentsCount(response.data.ongoing_students_count);
            setFinishedStudentsCount(response.data.finished_students_count);
            setLoaded(true);
            setIsNewData(true);
            setLazyLoading(false);
            // displayGlobalMessage("Data refreshed successfully", "success");
        } catch (error) {
            setLazyLoading(false);
            log.log(error);
            displayGlobalMessage("Failed to refresh data", "error");
        };
    }, [examId, debouncedSearchQuery, sortField, sortOrder, querySortOrder, ongoingStudentsCount, finishedStudentsCount]);

    // No need for handleManualRefresh anymore as it's handled by the RefreshContext

    useEffect(() => {
        fetchData(); // fetch data immediately on component mount
    }, [examId, debouncedSearchQuery, sortField, sortOrder, fetchData]);

    const downloadProctorCSV = async (e) => {
        try {
            var url = constants.base_url + `/exam/${examId}/proctor/downloadcsv`;
            const response = await httpService.get(url)
            fileDownload(
                response.data,
                examTitle + "_Proctor.csv",
                'text/csv'
            );
        } catch (error) {
            log.log(error);
        };
    };

    const showHeaderForDashboard = () => {
        return (
            <Breadcrumb>
                <Breadcrumb.Item href={`/courseware/course/${courseId}/content/labs`} style={{ textDecoration: 'underline' }}>Labs</Breadcrumb.Item>
                <Breadcrumb.Item active>Proctor</Breadcrumb.Item>
                <Breadcrumb.Item href="#"><Link to={`/courseware/course/${courseId}/content/labs/${examId}`} onClick={(e) => {
                    e.preventDefault();
                    const href = e.currentTarget.getAttribute('href');
                    window.location.href = href;
                }} style={{ textDecoration: 'underline' }}>{examTitle}</Link></Breadcrumb.Item>
            </Breadcrumb>
        )
    };

    // We've replaced this with the RefreshCountdown component

    const showTableForExam = () => {
        const cleanedIpRange = ipRange.split(',').filter(ip => ip.trim() !== '');
        log.log("cleaned IP Range", cleanedIpRange)

        return (
            <div>
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        marginLeft: "10px",
                        marginBottom: "20px",
                        marginTop: "20px",
                    }}
                >
                    <div style={{ marginRight: "20px" }}>
                        <span style={{ fontWeight: "bold" }}>Total students:</span>{" "}
                        <Button
                            style={{
                                border: "none",
                                cursor: "default",
                                padding: "0.2rem 0.5rem",
                                borderRadius: "0.25rem",
                                display: "inline-block",
                                marginRight: "20px",
                            }}
                            disabled
                        >
                            {ongoingStudentsCount + finishedStudentsCount}
                        </Button>
                    </div>

                    <div style={{ marginRight: "20px" }}>
                        <span style={{ fontWeight: "bold" }}>Valid IP Addresses:</span>{" "}

                        {cleanedIpRange.map((ip, index) => (
                            <Button
                                key={index} // Use index or a unique ID if available
                                style={{
                                    border: "none",
                                    cursor: "default",
                                    padding: "0.2rem 0.5rem",
                                    borderRadius: "0.25rem",
                                    display: "inline-block",
                                    marginRight: "5px",
                                    width: `${Math.max(ip.length * 8, 50)}px`, // Adjust width calculation
                                    height: "30px",
                                    textAlign: "left",
                                    fontSize: "15px"
                                }}
                                disabled
                                dangerouslySetInnerHTML={{ __html: ip }}
                            />
                        ))}
                    </div>
                </div>

                <div>
                    <div
                        style={{ display: "flex", flexWrap: "wrap", marginLeft: "10px", alignItems: "center" }}
                    >
                        <span style={{ fontWeight: "bold", marginRight: "10px" }}>
                            Add / Reduce Time (minutes):
                            <span>
                                <EditableCell
                                    modifyAll={true}
                                    studentsResponseData={studentsResponseData}
                                    setStudentsResponseData={setStudentsResponseData}
                                    examId={examId}
                                    fetchData={fetchData}
                                />
                            </span>
                        </span>
                        
                        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px", marginRight: "20px" }}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <span style={{ fontWeight: "bold", marginRight: "5px" }}>Next refresh in:</span>
                                <CountdownDisplay />
                            </div>

                            <RefreshButton
                                variant="info"
                                style={{ borderRadius: "5px" }}
                                disabled={lazyLoading}
                            >
                                {lazyLoading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        <span className="ms-1">Refreshing...</span>
                                    </>
                                ) : (
                                    "Refresh Now"
                                )}
                            </RefreshButton>

                            <Button
                                variant='primary'
                                style={{ borderRadius: "5px" }}
                                onClick={downloadProctorCSV}
                            >
                                Download as CSV
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const handleApprove = async (e, studentId, type) => {
        try {

            const response = await httpService.put(constants.base_url + "/exam/" + examId + "/proctor/approve/" + studentId + "?type=" + type)
            displayGlobalMessage("Approved Successfully", "success");
            setStudentsResponseData(studentsResponseData.map((student) => {
                if (student.student_id === studentId) {
                    student = response.data.student_exam;
                }
                return student;
            }));
        }
        catch (error) {
            if (error.response && error.response.status === 403) {
                displayGlobalMessage("Exam has ended, Can't approve now", "error");
            } else {
                displayGlobalMessage("Some error has occurred, Can't approve now", "error");
                log.log(error);
            }
        };
    }
    const loadingTemplate = (options) => {
        return (
            <div
                className="flex align-items-center"
                style={{ height: "17px", flexGrow: "1", overflow: "hidden" }}
            >
                <Skeleton width={"60%"} height="1rem" />
            </div>
        );
    };

    const handleSort = (event) => {
        setSortField(event.sortField ? event.sortField : "");
        setSortOrder(event.sortOrder);
        if (event.sortOrder === -1) {
            setQuerySortOrder("-");
        } else {
            setQuerySortOrder("");
        }
    };

    const tableHeader = () => {
        const [seconds, setSeconds] = useState(0);
        useEffect(() => {
            const parseExamStartTime = examStartTime;
            log.log("exam Start time:", examStartTime);
            const parsedStartTime = new Date(parseExamStartTime);
            if (parsedStartTime) {
                // Function to update the timer every second
                const updateTimer = () => {
                    const now = new Date();
                    const elapsedTime = Math.floor((now - parsedStartTime) / 1000);
                    setSeconds(elapsedTime);
                };

                // Set up an interval that updates the timer every second
                const intervalId = setInterval(updateTimer, 1000);

                // Clean up the interval when the component unmounts
                return () => clearInterval(intervalId);
            }
        }, []);

        // Format the count into hours, minutes, and seconds for better readability
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const displaySeconds = seconds % 60;

        // Format the time with leading zeros
        const formattedHours = String(hours).padStart(2, '0');
        const formattedMinutes = String(minutes).padStart(2, '0');
        const formattedSeconds = String(displaySeconds).padStart(2, '0');

        return (
            <div className="flex justify-content-between align-items-center">
                <MultiSelect
                    value={visibleColumns}
                    options={hiddenColumns}
                    onChange={(e) => setVisibleColumns(e.value)}
                    optionLabel="name"
                    display="chip"
                    filter
                    placeholder="Select Columns"
                    maxSelectedLabels={3}
                    style={{ width: "15rem" }}
                />
                {/* {!examEndTime?
                    (<div>
                        <div className="flex justify-content-between align-items-center">
                            <h2>Time Elapsed</h2>
                        </div>
                        <div className="flex justify-content-between align-items-center">
                            <h2>
                        &nbsp;&nbsp;&nbsp;{formattedHours}:{formattedMinutes}:{formattedSeconds}
                            </h2>
                        </div>
                    </div>):
                    null
                }  */}
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                        value={searchQuery}
                        onChange={handleSearchChange}
                        placeholder=" Search"
                    />
                </span>
            </div>
        );
    };

    const columns = [
        {
            selectionMode: "multiple",
            headerStyle: { width: "3rem" },
            frozen: true,
        },
        {
            headerStyle: { width: "3rem" },
            header: "S.No.",
            frozen: true,
            body: (data, options) => options.rowIndex + 1,
        },
        {
            field: "student_email",
            header: "Email",
            frozen: true,
            sortable: true,
            style: { width: "250px" },
        },
        {
            field: "student_name",
            header: "Name",
            sortable: true,
            hidden: !visibleColumns?.some((col) => col.name === "Name"),
            style: { width: "100px" },
        },
        {
            field: "student_roll_no",
            header: "Roll No",
            sortable: true,
            hidden: !visibleColumns?.some((col) => col.name === "Roll No"),
            style: { width: "100px" },
        },
        {
            field: "ipaddress",
            header: "IP Address",
            style: { width: "150px" },
        },
        {
            field: "newipadd",
            header: "New IP Address",
            hidden: !visibleColumns?.some((col) => col.name === "NewIP"),
            style: { width: "150px" },
        },
        {
            field: "start_time",
            header: "Start Time",
            sortable: true,
            style: { width: "150px" },
            body: (data) => data.start_time ? format(new Date(data.start_time), "yyyy-MM-dd HH:mm") : "Not Started",
        },
        {
            field: "duration",
            header: "Timer",
            sortable: true,
            style: { width: "150px" },
            body: (data) => data.is_finished === false ? <>
                {
                    (data?.end_time) ?
                        <Countdown
                            date={
                                new Date(data.end_time).getTime()
                            }
                            renderer={countdownRenderer}
                        />
                        :
                        <Countdown
                            date={
                                new Date(data.start_time).getTime() +
                                moment.duration(data.duration).asMilliseconds()
                            }
                            renderer={countdownRenderer}
                        />
                }
            </> : "00:00:00:00",
        },
        {
            header: "Add / Reduce Time (minutes)",
            style: { width: "150px" },
            body: (data) => <EditableCell
                modifyAll={false}
                studentId={data.student_id}
                studentsResponseData={studentsResponseData}
                setStudentsResponseData={setStudentsResponseData}
                examId={examId}
                fetchData={fetchData}
            />,
        },
        {
            field: "added_time",
            header: "Total Extra Time (minutes)",
            style: { width: "150px" },
            body: (data) => {
                const totalSeconds = data.added_time || 0;
                const minutes = Math.floor(totalSeconds / 60);
                const seconds = totalSeconds % 60;
                return `${minutes}m ${seconds}s`;
            },
        },
        {
            field: "is_approved",
            header: "Status",
            sortable: true,
            style: { width: "250px", height: "auto" },
            body: (data) => data.is_approved === true ? <Button variant="light" style={{ fontWeight: 'bold' }} >Approved</Button> :
                !data.start_time ?
                    <DropdownButton variant="danger" style={{ fontWeight: 'bold', height: "100px" }} title="Approve Latestart">
                        <Dropdown.Item style={{ height: "auto" }} onClick={(e) => handleApprove(e, data.student_id, 'remainingTime')}>Approve with
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip id="tooltip-left">
                                        Approve the student with remaining time
                                    </Tooltip>
                                }
                            >
                                <span> Remaining Time </span>
                            </OverlayTrigger>
                        </Dropdown.Item>
                        <Dropdown.Item style={{ height: "auto" }} onClick={(e) => handleApprove(e, data.student_id, 'fullTime')}>Approve with
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip id="tooltip-left">
                                        Approve the student with full time
                                    </Tooltip>
                                }
                            >
                                <span> Full Time </span>
                            </OverlayTrigger>
                        </Dropdown.Item>
                    </DropdownButton>
                    :
                    <Button variant="danger" style={{ fontWeight: 'bold' }} onClick={(e) => handleApprove(e, data.student_id, "noChange")}>Approve IP</Button>,
        }
    ];

    const showTableForListOfStudents = () => {
        return (
            <div>
                {loaded ? (
                    <div className="cont">
                        {/* <Navbar navbarClass="navbar-dark fixed-top" /> */}
                        {showHeaderForDashboard()}
                        <hr
                            style={{
                                height: "1px",
                                border: "none",
                                backgroundColor: "grey",
                            }}
                        />
                        {showTableForExam()}

                        <hr
                            style={{
                                height: "1px",
                                border: "none",
                                backgroundColor: "grey",
                            }}
                        />
                        {
                            (ongoingStudentsCount + finishedStudentsCount === 0) ? (
                                <h1
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginTop: "300px"
                                    }}
                                >
                                    No student has started the exam
                                </h1>
                            ) : (
                                <TableComponent
                                    /*Mandatory props*/

                                    //Data to be displayed
                                    tableData={studentsResponseData}
                                    //Definition of columns to be displayed
                                    columns={columns}
                                    //Height of the table
                                    scrollHeight="600px"
                                    /*Optional props
                                    Required for Infinte scrolling
                                    Server Side searching and Sorting*/

                                    //Change table data
                                    setTableData={setStudentsResponseData}
                                    //Table header
                                    tableHeader={tableHeader}
                                    //To display the loading template. Loading template will be rendered if lazyLoading is true
                                    lazyLoading={lazyLoading}
                                    //To set lazyloding
                                    setLazyLoading={setLazyLoading}
                                    //Template to diplay while data is loading
                                    loadingTemplate={loadingTemplate}
                                    //To check if new data is loaded
                                    isNewData={isNewData}
                                    //To set the value of isNewData
                                    setIsNewData={setIsNewData}
                                    // api of data
                                    api={`${constants.base_url}/exam/${examId}/proctor`}
                                    // location of data in response
                                    location={"data.student_exam.results"}
                                    debouncedSearchQuery={debouncedSearchQuery}
                                    querySortOrder={querySortOrder}
                                    sortField={sortField}
                                    sortOrder={sortOrder}
                                    handleSort={handleSort}
                                    stripedRows={false}
                                    showGridlines={false}
                                    selectedRowData={selectedRows}
                                    setSelectedRowData={setSelectedRows}
                                    isSelectAllChecked={isSelectAllChecked}
                                    setIsSelectAllChecked={setIsSelectAllChecked}
                                    isServerSide={true}
                                    isInfiniteScroll={true}
                                    selectionMode={"checkbox"}
                                    numberOfROws={ongoingStudentsCount + finishedStudentsCount}
                                />
                            )
                        }

                    </div>
                ) : (
                    <div className="h-screen flex justify-content-center align-items-center">
                        <Spinner animation="border" />
                    </div>
                )}

                <br />
                <br />
            </div>
        )
    };

    return (
        <div>
            <RefreshProvider interval={120} onRefresh={fetchData} isExternallyLoading={lazyLoading}>
                {showTableForListOfStudents()}
            </RefreshProvider>
        </div>
    );
}

export default LiveDashboard;